import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {
  useDocumentCraftingModels,
  useRefineDocumentCraftingDraft,
} from "../../../Query/useDocumentCrafting";
import ModelSelect, {
  resolveDocumentCraftingModelId,
} from "../Common/ModelSelect";

/** Same limits as the change request of the revise form. */
export const CHANGE_REQUEST_MAX = 4000;

interface Props {
  open: boolean;
  documentId: number;
  revisionId: number;
  /** The draft's model, preselected while it is accessible. */
  modelId: number | null;
  onClose: () => void;
  /** The refinement run started. */
  onStarted: (runId: number) => void;
}

/** Another change request on top of the draft (same draft, new run). */
const RefineDialog: React.FC<Props> = ({
  open,
  documentId,
  revisionId,
  modelId,
  onClose,
  onStarted,
}) => {
  const { t } = useTranslation("documentCrafting");
  const models = useDocumentCraftingModels();
  const refine = useRefineDocumentCraftingDraft();
  const [changeRequest, setChangeRequest] = useState("");
  const [model, setModel] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);

  const text = changeRequest.trim();
  const missing = !text;
  const tooLong = text.length > CHANGE_REQUEST_MAX;
  const resolvedModel = resolveDocumentCraftingModelId(models.data, model, modelId);

  const submit = () => {
    setTouched(true);
    if (missing || tooLong || resolvedModel === null) return;
    refine.mutate(
      {
        documentId,
        revisionId,
        payload: { change_request: text, model_id: resolvedModel },
      },
      { onSuccess: (response) => onStarted(response.run_id) },
    );
  };

  const reset = () => {
    setChangeRequest("");
    setModel(null);
    setTouched(false);
  };

  return (
    <Dialog
      open={open}
      onClose={refine.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: reset }}
    >
      <DialogTitle>{t("detail.refine.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("detail.refine.text")}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          label={t("detail.refine.label")}
          placeholder={t("detail.refine.placeholder")}
          value={changeRequest}
          onChange={(event) => setChangeRequest(event.target.value)}
          error={(touched && missing) || tooLong}
          helperText={
            touched && missing
              ? t("detail.refine.required")
              : `${text.length} / ${CHANGE_REQUEST_MAX}`
          }
          disabled={refine.isPending}
          required
        />
        <Box sx={{ mt: 2 }}>
          <ModelSelect
            value={model}
            onChange={setModel}
            preferredModelId={modelId}
            disabled={refine.isPending}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose} disabled={refine.isPending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={refine.isPending || tooLong || resolvedModel === null}
        >
          {t("detail.refine.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefineDialog;
